import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ELEMENT_ACTION,
  ICON_POSITIONS,
  ICON_TYPES,
  NOTIFICATION_TYPES,
} from '../../../shared/constants';
import { IContentfulLink } from '../../../types/shared';

import Spinner from '../../atoms/icons/Spinner';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { getCtaLink } from '../../../utils/generic';
import { languageHelper } from '../../../utils/language';
import { LABELS } from '../../../utils/language/form-labels';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { Body, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import OkayIcon from '../../../images/okay-checkmark-icon.svg';
import ErrorIcon from '../../../images/warning-icon.svg';

interface Props {
  title?: string;
  show: boolean;
  statusType: string;
  primaryButtonLink?: IContentfulLink;
  primaryButtonLabel?: string;
  customPrimaryClick?: () => void;
  secondaryButtonUrl?: string;
  children?: ReactNode;
  hasShadow?: boolean;
}

const FormStatusCard = ({
  title,
  show,
  statusType,
  primaryButtonLink,
  primaryButtonLabel,
  customPrimaryClick,
  secondaryButtonUrl,
  children,
  hasShadow = true,
}: Props) => {
  const { lang } = languageHelper();

  const renderImage = () => {
    switch (statusType) {
      case NOTIFICATION_TYPES.loading:
        return (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        );
      case NOTIFICATION_TYPES.error:
        return (
          <ImageWrapper>
            <Image src={ErrorIcon} data-testid="error-icon" />
          </ImageWrapper>
        );
      case NOTIFICATION_TYPES.success:
      default:
        return (
          <ImageWrapper>
            <Image src={OkayIcon} data-testid="success-icon" />
          </ImageWrapper>
        );
    }
  };

  if (show) {
    return (
      <Container isCardDesign={hasShadow} data-testid="status-card">
        {renderImage()}
        <InnerCard>
          <CopyWrapper>
            {title && <Title>{title}</Title>}
            {children && <Body>{children}</Body>}
          </CopyWrapper>
          {statusType === NOTIFICATION_TYPES.error && (
            <ButtonContainer>
              {(customPrimaryClick || primaryButtonLink) && (
                <PrimaryButton
                  icon={ICON_TYPES.none}
                  buttonTheme={BUTTON_THEMES.highlight}
                  label={primaryButtonLabel || LABELS.again[lang]}
                  type={BUTTON_TYPES.internal}
                  iconPosition={
                    primaryButtonLink?.iconPosition || ICON_POSITIONS.end
                  }
                  buttonAction={BUTTON_TYPES.button}
                  onButtonClick={customPrimaryClick}
                  to={primaryButtonLink && getCtaLink(primaryButtonLink)}
                />
              )}
              {secondaryButtonUrl && (
                <SecondaryButton
                  type={BUTTON_TYPES.internal}
                  to={secondaryButtonUrl}
                  icon={ICON_TYPES.default}
                  label={LABELS.back[lang]}
                  deqComponentTitle="close-status-card"
                  deqCustomAttributes={{
                    componentType: 'form-status-card',
                    elementTarget: secondaryButtonUrl,
                    elementAction: ELEMENT_ACTION.close,
                  }}
                />
              )}
            </ButtonContainer>
          )}
        </InnerCard>
      </Container>
    );
  }
  return null;
};

export default FormStatusCard;

const Container = styled.div<{ isCardDesign: boolean }>`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${Spacings.S};

  ${({ isCardDesign }) =>
    isCardDesign &&
    css`
      background-color: ${colors.white};
      border-radius: ${({ theme }) => theme.radiuses.M};
      box-shadow: ${({ theme }) => theme.elevations.abnElevationTile};
      padding: ${Spacings.S};

      ${mq.from.breakpoint.M`
        padding: ${Spacings.L};
      `}
    `};
`;

const InnerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.S};
`;

const CopyWrapper = styled.div``;

const Title = styled(IntroText)`
  display: block;
  margin-bottom: ${Spacings.XS};
`;

const SpinnerWrapper = styled.div`
  flex-shrink: 0;
  height: 56px;
  width: 65px;
`;

const ImageWrapper = styled.div`
  flex-shrink: 0;
  height: 80px;
  width: 80px;
`;

const Image = styled.img`
  display: block;
  height: 100%;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: inline-flex;
  flex-direction: row;
  gap: ${Spacings.M};

  ${mq.to.breakpoint.M`
    gap: ${Spacings.S};
    flex-direction: column;
  `}
`;
